import { ProjectCategoryDto } from '@cradua/data';
import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as ProjectCategoryActions from './project-category.actions';

export const PROJECT_CATEGORY_FEATURE_KEY = 'projectcategory';

export interface ProjectCategoryState extends EntityState<ProjectCategoryDto> {
  selectedProjectCategoryId?: string | number; // which ProjectCategory record has been selected
  loaded: boolean; // has the ProjectCategory list been loaded
  error?: string | null; // last none error (if any)
}

export interface ProjectCategoryPartialState {
  readonly [PROJECT_CATEGORY_FEATURE_KEY]: ProjectCategoryState;
}

export const projectCategoryAdapter: EntityAdapter<ProjectCategoryDto> = createEntityAdapter<
  ProjectCategoryDto
>({
  selectId: (projectCategory) => projectCategory.id,
  sortComparer: false,
});

export const initialState: ProjectCategoryState = projectCategoryAdapter.getInitialState(
  {
    // set initial required properties
    loaded: false,
    error: null,
    selectedProjectCategoryId: null,
  }
);

const projectCategoryReducer = createReducer(
  initialState,
  on(ProjectCategoryActions.loadProjectCategory, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ProjectCategoryActions.loadProjectCategorySuccess,
    (state, { projectCategory }) =>
      projectCategoryAdapter.addAll(projectCategory, {
        ...state,
        loaded: false,
      })
  ),
  on(ProjectCategoryActions.loadProjectCategoryFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),
  on(ProjectCategoryActions.addProjectCategory, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ProjectCategoryActions.addProjectCategorySuccess,
    (state, { newProjectCategory }) =>
      projectCategoryAdapter.addMany(newProjectCategory, {
        ...state,
        loaded: false,
      })
  ),
  on(ProjectCategoryActions.addProjectCategoryFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),
  on(ProjectCategoryActions.updateProjectCategory, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ProjectCategoryActions.updateProjectCategorySuccess,
    (state, { updateProjectCategory }) =>
      projectCategoryAdapter.upsertMany(updateProjectCategory, {
        ...state,
        loaded: false,
      })
  ),
  on(
    ProjectCategoryActions.updateProjectCategoryFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(ProjectCategoryActions.removeProjectCategory, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(ProjectCategoryActions.removeProjectCategorySuccess, (state, { ids }) =>
    projectCategoryAdapter.removeMany(ids, { ...state, loaded: false })
  ),
  on(
    ProjectCategoryActions.removeProjectCategoryFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  )
);

export function reducer(
  state: ProjectCategoryState | undefined,
  action: Action
) {
  return projectCategoryReducer(state, action);
}
